import { useMemo } from 'react';
import { createFlagCreationSuggestionAdapter } from '@gonfalon/flag-creation/flagCreationSuggestionAdapter';
import { useAgentPromptMutation, useFlagCreationSuggestionMutation } from '@gonfalon/observability-graphql/hooks';
import { VegaProjectProvider } from '@gonfalon/observability-ui/hooks/VegaProjectProvider';
import { useAgentInsights } from '@gonfalon/vega/AgentInsightsContext';
import type { AgentPromptMutationFn } from '@gonfalon/vega/components/AgentInsights/AgentInsights';

import { VegaPanelBody } from './VegaPanel';
import { VegaWrappers } from './VegaWrappers';

// Routing key for the dedicated flag_creation_suggestion mutation. Kept as a
// literal rather than the AgentRequestType enum (which is expected to be
// retired) so this panel depends only on the request-type value the agent
// framework sends, not on the generated enum symbol.
const FLAG_CREATE_REQUEST_TYPE = 'FlagCreate';

const isFlagCreateRequest = (value: unknown): boolean => value === FLAG_CREATE_REQUEST_TYPE;

/**
 * Vega panel for the flags page, which serves both flag cleanup and flag
 * creation through one drawer. Flag-create conversations route to the dedicated
 * `flag_creation_suggestion` endpoint (single-skill, gen-AI-gate-exempt,
 * read-write, server-tuned config); every other request type (e.g. flag
 * cleanup) uses the generic `agentPrompt`. Routing is by `request_type`.
 *
 * Because the flag-create endpoint is gen-AI-gate-exempt, we also exempt the
 * drawer's account gen-AI gate while a flag-create conversation is active
 * (`requestType === FlagCreate`). Every other request type stays gated, so flag
 * cleanup still requires the account opt-in.
 *
 * Renders the shared `VegaPanelBody`, supplying the request-type-routed mutation
 * and the gate exemption as props — the body defaults `nonGenAiSkill` to false,
 * so the generic `VegaPanel` (and every other caller) stays gate-generic.
 */
const FlagToolsVegaPanelInner = ({ children }: React.PropsWithChildren) => {
  const { requestType } = useAgentInsights();
  const [agentPromptMutation, { loading: agentSending }] = useAgentPromptMutation();
  const [flagCreationMutation, { loading: flagCreationSending }] = useFlagCreationSuggestionMutation();

  const routedMutation = useMemo<AgentPromptMutationFn>(() => {
    const flagCreateMutation = createFlagCreationSuggestionAdapter(flagCreationMutation);
    // Route on the client-side request kind from context (request_type is no
    // longer part of the GraphQL agentPrompt input).
    return (options) => (isFlagCreateRequest(requestType) ? flagCreateMutation(options) : agentPromptMutation(options));
  }, [agentPromptMutation, flagCreationMutation, requestType]);

  return (
    <VegaPanelBody
      agentPromptMutation={routedMutation}
      sending={agentSending || flagCreationSending}
      nonGenAiSkill={isFlagCreateRequest(requestType)}
    >
      {children}
    </VegaPanelBody>
  );
};

export const FlagToolsVegaPanel = ({ children }: React.PropsWithChildren) => {
  return (
    <VegaWrappers>
      <VegaProjectProvider>
        <FlagToolsVegaPanelInner>{children}</FlagToolsVegaPanelInner>
      </VegaProjectProvider>
    </VegaWrappers>
  );
};
